import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { useSession } from "@vega-api/custom";
import { useGetCustomHeaders } from "@vega-api/custom/stores/useGetCustomHeaders";
import {
  useCreateOrUpdateSavedFilter,
  useDeleteSavedFilter,
  useGetFiltersByTableCode,
} from "@vega-api/generated/filters-v1/filters-v1";
import {
  type FilterState,
  type SavedFiltersHook,
  type TableFilterId,
} from "@vega-modules/components/Table/TableFilters/types";

export const FILTER_STATE_KEY = "FilterState";

const getUniqueName = (name: string, existingNames: string[]): string => {
  const trimmedName = name.trim();
  const normalizedExistingNames = new Set(existingNames.map((n) => n.trim()));

  if (!normalizedExistingNames.has(trimmedName)) return trimmedName;

  let counter = 1;
  while (normalizedExistingNames.has(`${trimmedName} (${counter})`)) {
    counter++;
  }

  return `${trimmedName} (${counter})`;
};

export const useSavedFilters = (
  id: TableFilterId,
  applyContextInPersistenceKeys = true,
): SavedFiltersHook => {
  const { contextKey } = useGetCustomHeaders();
  const { user } = useSession();
  const queryClient = useQueryClient();

  const tableCode = useMemo(
    () => (applyContextInPersistenceKeys ? `${id}.${contextKey}` : id),
    [id, contextKey, applyContextInPersistenceKeys],
  );
  const { data, queryKey } = useGetFiltersByTableCode(tableCode);
  const { mutate: createFilter, isPending: isSaving } =
    useCreateOrUpdateSavedFilter();
  const { mutate: deleteFilterMutation, isPending: isDeleting } =
    useDeleteSavedFilter();

  const savedFilters = useMemo(
    () =>
      data?.data?.map((filter) => ({
        id: filter.id!,
        filterName: filter.filterName!,
        filterConfig: filter.filterConfig || {},
      })) ?? [],
    [data?.data],
  );

  const hasFilters = savedFilters.length > 0;

  const saveFilter = useCallback(
    async (name: string, filterState: FilterState): Promise<void> => {
      const uniqueName = getUniqueName(
        name,
        savedFilters.map((f) => f.filterName),
      );

      return new Promise((resolve, reject) => {
        createFilter(
          {
            data: {
              filterName: uniqueName,
              tableCode,
              userId: user?.id ?? "",
              filterConfig: {
                [FILTER_STATE_KEY]: JSON.stringify(filterState),
              },
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey });
              resolve();
            },
            onError: reject,
          },
        );
      });
    },
    [createFilter, tableCode, user?.id, queryClient, queryKey, savedFilters],
  );

  const deleteFilter = useCallback(
    async (filterId: string): Promise<void> =>
      new Promise((resolve, reject) => {
        deleteFilterMutation(
          { filterId },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey });
              resolve();
            },
            onError: reject,
          },
        );
      }),
    [deleteFilterMutation, queryClient, queryKey],
  );

  const loadFilter = useCallback(
    (filterId: string): FilterState | undefined => {
      const filter = savedFilters.find((f) => f.id === filterId);

      if (!filter?.filterConfig[FILTER_STATE_KEY]) return undefined;

      try {
        return JSON.parse(filter.filterConfig[FILTER_STATE_KEY] as string);
      } catch {
        return undefined;
      }
    },
    [savedFilters],
  );

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  return {
    savedFilters,
    hasFilters,
    saveFilter,
    deleteFilter,
    loadFilter,
    isSaving,
    isDeleting,
    invalidateQuery,
  };
};
